import { memo, useId } from "react";
import { Venue, VenuePinType } from "common/types";
import { hasVerifiedData } from "common/utils/maps";
import { cn } from "@/lib/utils";
import { BusinessTypeGlyph } from "@/components/Map/BusinessTypeGlyph";
import {
  PIN_COLORS_LIGHT,
  getVenuePinColor,
} from "@/components/Map/pinColors";
import { useDarkMode } from "@/hooks/useDarkMode";
import OrangePin from "@/assets/pins/orange_pin.svg";
import OrangePinDark from "@/assets/pins/orange_pin_dark.svg";
import GreyPin from "@/assets/pins/grey_pin.svg";
import BluePin from "@/assets/pins/blue_pin.svg";
import PurplePin from "@/assets/pins/purple_pin.svg";
import GreenPin from "@/assets/pins/green_pin.svg";
import BlackPin from "@/assets/pins/black_pin.svg";

// Slightly bigger than Google's own POI markers for better visibility.
export const SIZE_DEFAULT = 30;
export const SIZE_ACTIVE = 40;
export const SIZE_SELECTED = 38;

// orange_pin_blank.svg viewBox dimensions (shared circle-head + tail shape).
const PIN_W = 42;
const PIN_H = 38;
export const PIN_ASPECT = PIN_H / PIN_W;
// White inner-circle centre within the frame (glyph position).
const CIRCLE_CX = 18.5;
const CIRCLE_CY = 16.5;
// Frame is left-of-centre (badge overflows right); shift so the tail tip
// lands on the marker's bottom-centre anchor.
const FRAME_SHIFT_X = PIN_W / 2 - CIRCLE_CX;

// Re-exported from the palette so off-map pins (autosuggest) can't drift from it.
export const ORANGE = PIN_COLORS_LIGHT[VenuePinType.Orange];
export const GREY = PIN_COLORS_LIGHT[VenuePinType.Grey];
// Dark-grey outline for the hollow verified pin body (Google POI look).
export const RING_STROKE = "#464649";

// Face + glyph come from tokens so both invert with the theme; the orange pin
// floods its face on hover, so anything drawn on it flips to the on-filled tone.
const PIN_FACE = "var(--container-neutral-minimal)";
const GLYPH_COLOR = "var(--foreground-neutral-strong)";
const ON_FILLED = "var(--foreground-neutral-on-filled-adaptive)";

// Inset hover ring, scaled from the 49x54 design frame by 16.5/18.1319 so its
// outer edge (13.59 + 1.82/2) lands exactly on the r=14.5 face circle.
const HOVER_RING_R = 13.59;
const HOVER_RING_W = 1.82;

// "Sociability Verified" blue tick badge (Figma asset), theme-aware.
const TICK_BADGE_LIGHT = "#352FB8";
const TICK_BADGE_DARK = "#4D3DC4";
// The tick's own size in the pin's 42x38 frame. The Figma export pads it out to
// a 29x29 shadow box, so the nested svg is scaled off that padding rather than
// the tick itself — sizing the box directly is what left the tick undersized.
const TICK_BADGE_SIZE = 16;
const TICK_EXPORT_BOX = 29;
const TICK_EXPORT_SHAPE = 18;
const TICK_SCALE = TICK_BADGE_SIZE / TICK_EXPORT_SHAPE;
const TICK_SVG_SIZE = TICK_EXPORT_BOX * TICK_SCALE;
// The shape starts 5.5 units into the export box.
const TICK_PAD = 5.5 * TICK_SCALE;
// Top-right, its top level with the head circle, right edge inside the frame.
const TICK_BADGE_X = 23.2 - TICK_PAD;
const TICK_BADGE_Y = -TICK_PAD;

export const NAME_TEXT_COLOR = "#3c4043";
export const CATEGORY_TEXT_COLOR = "#70757a";

// White halo so label text stays legible over any map tile (Google POI look).
export const NAME_HALO =
  "0 1px 2px #fff, 1px 0 2px #fff, -1px 0 2px #fff, 0 -1px 2px #fff";

/** Vertical centre of the pin's white circle, for aligning a side label.
 * Shared with BasicMapView's separate label markers for the same anchor. */
export function getPinCentreTop(size: number): number {
  return size * PIN_ASPECT * (CIRCLE_CY / PIN_H);
}

// Flat "classic" pin artwork used for the selected state, one per pin colour.
const SELECTED_PIN_ASSETS: Record<VenuePinType, string> = {
  [VenuePinType.Green]: GreenPin,
  [VenuePinType.Black]: BlackPin,
  [VenuePinType.Blue]: BluePin,
  [VenuePinType.Purple]: PurplePin,
  [VenuePinType.Orange]: OrangePin,
  [VenuePinType.Grey]: GreyPin,
};

/** Selected-pin artwork; user view has only two states, mapper view has all six.
 * The orange asset bakes in a fixed "S" glyph colour, so dark mode needs its
 * own variant (light "S" reads poorly against the pin's dark-mode orange). */
export function getSelectedPinAsset(
  pinType: VenuePinType | undefined,
  isMapperView: boolean,
  isDark = false,
): string {
  const type = pinType ?? VenuePinType.Grey;

  if (isMapperView) {
    return SELECTED_PIN_ASSETS[type];
  }

  if (type === VenuePinType.Grey) {
    return GreyPin;
  }

  return isDark ? OrangePinDark : OrangePin;
}

// Circle-head + tail silhouette (native viewBox space), shared by grey and
// verified pins.
const HEAD_TAIL_PATH =
  "M19.7887 36.9141C19.0441 37.5415 17.9559 37.5415 17.2113 36.9141L6.66718 28.0294C5.24085 26.8276 6.09074 24.5 7.95591 24.5L29.0441 24.5C30.9093 24.5 31.7592 26.8276 30.3328 28.0294L19.7887 36.9141Z";

/** Recolorable pin frame: round head + tail, white inner circle for the
 * glyph, and an "S" badge. `color` = orange (has data) or grey (no data). */
export function SociabilityPin({ size, color }: { size: number; color: string }) {
  return (
    <svg
      width={size}
      height={size * PIN_ASPECT}
      viewBox="0 0 42 38"
      fill="none"
      style={{
        display: "block",
        transform: `translateX(${(FRAME_SHIFT_X / PIN_W) * size}px)`,
      }}
      aria-hidden="true"
    >
      <circle cx="18.5" cy="16.5" r="16.5" fill={color} />
      <path
        d="M19.7887 36.9141C19.0441 37.5415 17.9559 37.5415 17.2113 36.9141L6.66718 28.0294C5.24085 26.8276 6.09074 24.5 7.95591 24.5L29.0441 24.5C30.9093 24.5 31.7592 26.8276 30.3328 28.0294L19.7887 36.9141Z"
        fill={color}
      />
      <circle cx="18.5" cy="16.5" r="14.5" fill={PIN_FACE} />
      {/* "S" badge (top-right) */}
      <path
        d="M33.637 18.7852C32.8579 18.7852 32.2805 18.2432 32.0518 17.2997C31.8668 16.5349 31.7896 16.3023 31.7884 16.3C31.1068 14.1477 30.073 12.9607 29.1614 11.9129C28.1363 10.7344 27.2507 9.71757 27.2172 7.75602C27.1867 5.96461 27.8401 4.31528 29.0571 3.11223C30.2638 1.91948 31.9383 1.24577 33.7724 1.21598C33.8133 1.21541 33.8531 1.21484 33.894 1.21484C37.5974 1.21484 40.5529 4.00019 40.7678 7.69415C40.8657 9.37728 40.516 11.0621 39.7277 12.7011C39.0368 14.1379 37.9858 15.585 36.604 17.0024C35.4509 18.1848 34.4523 18.7852 33.637 18.7852Z"
        fill={color}
      />
      <path
        d="M33.894 1.42966C37.4978 1.42966 40.345 4.15258 40.5525 7.70675C40.7559 11.1985 38.9983 14.2388 36.4491 16.8534C35.2194 18.1143 34.3014 18.5703 33.6376 18.5703C32.8585 18.5703 32.4298 17.9413 32.2621 17.2493C32.0702 16.4581 31.9936 16.2318 31.9936 16.2318C30.5374 11.6339 27.4943 11.3509 27.4332 7.752C27.3722 4.15372 30.1168 1.49096 33.7759 1.43081C33.8157 1.43024 33.8543 1.42966 33.894 1.42966ZM33.894 1C33.8525 1 33.811 1 33.769 1.00115C31.8783 1.03208 30.1508 1.72813 28.9044 2.95983C27.6453 4.20471 26.9694 5.90904 27.0011 7.75945C27.0356 9.79892 27.9887 10.8937 28.9983 12.0532C29.8967 13.085 30.9143 14.2542 31.5816 16.3607L31.5839 16.3687C31.5839 16.3687 31.6605 16.6013 31.8415 17.3495C32.0916 18.383 32.7629 19 33.637 19C34.5112 19 35.564 18.3784 36.7591 17.1525C38.157 15.7185 39.2214 14.2525 39.9226 12.7945C40.7271 11.1223 41.0838 9.40191 40.9835 7.68211C40.8769 5.84946 40.0995 4.15945 38.7949 2.92432C37.4846 1.68345 35.7438 1 33.894 1Z"
        fill="#FAFAFA"
      />
      <path
        d="M32.1855 9.74334C32.6153 10.2412 33.3881 10.6829 34.1602 10.6582C34.7526 10.6393 35.1156 10.3414 35.1018 9.92093C35.0851 9.40992 34.5693 9.2455 33.8859 9.01177C32.7012 8.62851 31.3079 8.20629 31.2595 6.70305C31.2151 5.335 32.3163 4.41151 34.0248 4.35709C35.251 4.31756 36.3274 4.75009 36.9313 5.51317C36.9808 5.56816 36.9756 5.63576 36.9365 5.67873L36.0289 6.61081C36.0191 6.62112 35.9926 6.64117 35.954 6.64232C35.916 6.64347 35.8872 6.62513 35.8774 6.61539C35.3789 6.13474 34.7923 5.91247 34.2 5.93194C33.6214 5.95028 33.3397 6.20005 33.3512 6.54607C33.3621 6.8921 33.6704 7.03245 34.4524 7.32348C35.4994 7.71132 37.1814 8.1089 37.2361 9.79261C37.2839 11.2661 36.0721 12.1775 34.3498 12.2331C32.9582 12.2778 31.7959 11.7433 31.1062 10.892C31.0901 10.8731 31.0688 10.8519 31.0676 10.8118C31.0664 10.7717 31.0832 10.7447 31.101 10.7264L32.0351 9.74849C32.0604 9.72501 32.0829 9.71756 32.11 9.71641C32.1371 9.71527 32.1688 9.72271 32.1866 9.74334H32.1855Z"
        fill="#FAFAFA"
      />
      <path
        d="M33.8936 0.75C35.8054 0.75 37.6087 1.45712 38.9668 2.74316C40.3186 4.02309 41.1232 5.77341 41.2334 7.66797C41.3362 9.4332 40.9688 11.196 40.1475 12.9033C39.432 14.3905 38.3508 15.8785 36.9385 17.3271C35.7357 18.5609 34.6192 19.25 33.6367 19.25C32.6164 19.2499 31.8678 18.5207 31.5986 17.4082C31.5087 17.0361 31.4443 16.7932 31.4033 16.6445C31.3829 16.5705 31.3688 16.5198 31.3594 16.4883C31.3547 16.4726 31.3507 16.4618 31.3486 16.4551C31.3476 16.4519 31.3471 16.4496 31.3467 16.4482V16.4473L31.3438 16.4375L31.3428 16.4355C30.6918 14.3812 29.7036 13.2445 28.8096 12.2178C27.7983 11.0563 26.7872 9.90075 26.751 7.76367C26.7182 5.84853 27.4186 4.07746 28.7285 2.78223C30.0238 1.50224 31.8135 0.784582 33.7617 0.751953C33.8076 0.750704 33.8527 0.75 33.8936 0.75Z"
        stroke={color}
        strokeWidth="0.5"
      />
    </svg>
  );
}

/** Tick glyph + drop-shadow filter, shared by the nested (composed-pin) and
 * standalone (flat selected-pin overlay) badge wrappers below. IDs are
 * per-instance (useId) so many pins on the same map don't clash on shared
 * <mask>/<filter> references. */
function TickBadgeGraphic({ isDark }: { isDark: boolean }) {
  const uid = useId().replace(/:/g, "");
  const filterId = `tick-shadow-${uid}`;
  const maskId = `tick-mask-${uid}`;
  const color = isDark ? TICK_BADGE_DARK : TICK_BADGE_LIGHT;

  return (
    <>
      <g filter={`url(#${filterId})`}>
        <mask
          id={maskId}
          maskUnits="userSpaceOnUse"
          x="5.5"
          y="3.5"
          width="18"
          height="18"
          fill="black"
        >
          <rect fill="white" x="5.5" y="3.5" width="18" height="18" />
          <path d="M14.5 5.5C15.1525 5.5 15.643 5.96941 16.0742 6.38379C16.3204 6.61934 16.5745 6.86324 16.7969 6.95508C17.0012 7.04005 17.3399 7.04481 17.668 7.0498C18.278 7.05918 18.9696 7.06918 19.4502 7.5498C19.9308 8.03043 19.9408 8.72203 19.9502 9.33203C19.9552 9.66009 19.9599 9.99878 20.0449 10.2031C20.1368 10.4262 20.3807 10.6796 20.6162 10.9258C21.0306 11.3577 21.5 11.8475 21.5 12.5C21.5 13.1525 21.0306 13.643 20.6162 14.0742C20.3807 14.3204 20.1368 14.5745 20.0449 14.7969C19.9599 15.0012 19.9552 15.3399 19.9502 15.668C19.9408 16.278 19.9308 16.9696 19.4502 17.4502C18.9696 17.9308 18.278 17.9408 17.668 17.9502C17.3399 17.9552 17.0012 17.9599 16.7969 18.0449C16.5738 18.1368 16.3204 18.3807 16.0742 18.6162C15.6423 19.0306 15.1525 19.5 14.5 19.5C13.8475 19.5 13.357 19.0306 12.9258 18.6162C12.6796 18.3807 12.4255 18.1368 12.2031 18.0449C11.9988 17.9599 11.6601 17.9552 11.332 17.9502C10.722 17.9408 10.0304 17.9308 9.5498 17.4502C9.06918 16.9696 9.05918 16.278 9.0498 15.668C9.04481 15.3399 9.04005 15.0012 8.95508 14.7969C8.86324 14.5738 8.61934 14.3204 8.38379 14.0742C7.96941 13.6423 7.5 13.1525 7.5 12.5C7.5 11.8475 7.96941 11.357 8.38379 10.9258C8.61934 10.6796 8.86324 10.4255 8.95508 10.2031C9.04005 9.99878 9.04481 9.66009 9.0498 9.33203C9.0623 8.72203 9.06918 8.03043 9.5498 7.5498C10.0304 7.06918 10.722 7.05918 11.332 7.0498C11.6601 7.04481 12 7.04005 12.2031 6.95508C12.4262 6.86324 12.6796 6.61934 12.9258 6.38379C13.3577 5.96941 13.8475 5.5 14.5 5.5ZM17 10.5C16.9343 10.5 16.8693 10.5129 16.8086 10.5381C16.7479 10.5632 16.6929 10.6 16.6465 10.6465L13.5 13.793L12.3535 12.6465C12.2597 12.5527 12.1327 12.5 12 12.5C11.8673 12.5 11.7403 12.5527 11.6465 12.6465C11.5527 12.7403 11.5 12.8673 11.5 13C11.5 13.1327 11.5527 13.2597 11.6465 13.3535L13.1465 14.8535C13.1929 14.9 13.2479 14.9368 13.3086 14.9619C13.3693 14.9871 13.4343 15 13.5 15C13.5657 15 13.6307 14.9871 13.6914 14.9619C13.7521 14.9368 13.8071 14.9 13.8535 14.8535L17.3535 11.3535C17.4 11.3071 17.4368 11.2521 17.4619 11.1914C17.4871 11.1307 17.5 11.0657 17.5 11C17.5 10.9343 17.4871 10.8693 17.4619 10.8086C17.4368 10.7479 17.4 10.6929 17.3535 10.6465C17.3071 10.6 17.2521 10.5632 17.1914 10.5381C17.1307 10.5129 17.0657 10.5 17 10.5Z" />
        </mask>
        <path
          d="M14.5 5.5C15.1525 5.5 15.643 5.96941 16.0742 6.38379C16.3204 6.61934 16.5745 6.86324 16.7969 6.95508C17.0012 7.04005 17.3399 7.04481 17.668 7.0498C18.278 7.05918 18.9696 7.06918 19.4502 7.5498C19.9308 8.03043 19.9408 8.72203 19.9502 9.33203C19.9552 9.66009 19.9599 9.99878 20.0449 10.2031C20.1368 10.4262 20.3807 10.6796 20.6162 10.9258C21.0306 11.3577 21.5 11.8475 21.5 12.5C21.5 13.1525 21.0306 13.643 20.6162 14.0742C20.3807 14.3204 20.1368 14.5745 20.0449 14.7969C19.9599 15.0012 19.9552 15.3399 19.9502 15.668C19.9408 16.278 19.9308 16.9696 19.4502 17.4502C18.9696 17.9308 18.278 17.9408 17.668 17.9502C17.3399 17.9552 17.0012 17.9599 16.7969 18.0449C16.5738 18.1368 16.3204 18.3807 16.0742 18.6162C15.6423 19.0306 15.1525 19.5 14.5 19.5C13.8475 19.5 13.357 19.0306 12.9258 18.6162C12.6796 18.3807 12.4255 18.1368 12.2031 18.0449C11.9988 17.9599 11.6601 17.9552 11.332 17.9502C10.722 17.9408 10.0304 17.9308 9.5498 17.4502C9.06918 16.9696 9.05918 16.278 9.0498 15.668C9.04481 15.3399 9.04005 15.0012 8.95508 14.7969C8.86324 14.5738 8.61934 14.3204 8.38379 14.0742C7.96941 13.6423 7.5 13.1525 7.5 12.5C7.5 11.8475 7.96941 11.357 8.38379 10.9258C8.61934 10.6796 8.86324 10.4255 8.95508 10.2031C9.04005 9.99878 9.04481 9.66009 9.0498 9.33203C9.0623 8.72203 9.06918 8.03043 9.5498 7.5498C10.0304 7.06918 10.722 7.05918 11.332 7.0498C11.6601 7.04481 12 7.04005 12.2031 6.95508C12.4262 6.86324 12.6796 6.61934 12.9258 6.38379C13.3577 5.96941 13.8475 5.5 14.5 5.5ZM17 10.5C16.9343 10.5 16.8693 10.5129 16.8086 10.5381C16.7479 10.5632 16.6929 10.6 16.6465 10.6465L13.5 13.793L12.3535 12.6465C12.2597 12.5527 12.1327 12.5 12 12.5C11.8673 12.5 11.7403 12.5527 11.6465 12.6465C11.5527 12.7403 11.5 12.8673 11.5 13C11.5 13.1327 11.5527 13.2597 11.6465 13.3535L13.1465 14.8535C13.1929 14.9 13.2479 14.9368 13.3086 14.9619C13.3693 14.9871 13.4343 15 13.5 15C13.5657 15 13.6307 14.9871 13.6914 14.9619C13.7521 14.9368 13.8071 14.9 13.8535 14.8535L17.3535 11.3535C17.4 11.3071 17.4368 11.2521 17.4619 11.1914C17.4871 11.1307 17.5 11.0657 17.5 11C17.5 10.9343 17.4871 10.8693 17.4619 10.8086C17.4368 10.7479 17.4 10.6929 17.3535 10.6465C17.3071 10.6 17.2521 10.5632 17.1914 10.5381C17.1307 10.5129 17.0657 10.5 17 10.5Z"
          fill={color}
        />
        <path
          d="M16.0742 6.38379L15.0349 7.4654L15.0372 7.46757L16.0742 6.38379ZM16.7969 6.95508L17.3728 5.57005L17.3694 5.56863L16.7969 6.95508ZM17.668 7.0498L17.691 5.54998L17.6908 5.54998L17.668 7.0498ZM19.4502 7.5498L20.5109 6.48914V6.48914L19.4502 7.5498ZM19.9502 9.33203L21.45 9.30918L21.45 9.30898L19.9502 9.33203ZM20.0449 10.2031L21.4319 9.632L21.4299 9.62718L20.0449 10.2031ZM20.6162 10.9258L19.5324 11.9628L19.5338 11.9643L20.6162 10.9258ZM20.6162 14.0742L19.5346 13.0349L19.5324 13.0372L20.6162 14.0742ZM20.0449 14.7969L21.4299 15.3728L21.4314 15.3694L20.0449 14.7969ZM19.9502 15.668L21.45 15.691L21.45 15.6908L19.9502 15.668ZM17.668 17.9502L17.6908 19.45L17.691 19.45L17.668 17.9502ZM16.7969 18.0449L17.368 19.4319L17.3728 19.4299L16.7969 18.0449ZM16.0742 18.6162L15.0372 17.5324L15.0357 17.5338L16.0742 18.6162ZM12.9258 18.6162L13.9651 17.5346L13.9628 17.5324L12.9258 18.6162ZM12.2031 18.0449L11.6272 19.4299L11.6306 19.4314L12.2031 18.0449ZM11.332 17.9502L11.309 19.45L11.3092 19.45L11.332 17.9502ZM9.5498 17.4502L8.48914 18.5109H8.48914L9.5498 17.4502ZM9.0498 15.668L7.54998 15.6908L7.54998 15.691L9.0498 15.668ZM8.95508 14.7969L7.56805 15.368L7.57006 15.3728L8.95508 14.7969ZM8.38379 14.0742L9.46757 13.0372L9.46615 13.0357L8.38379 14.0742ZM8.38379 10.9258L9.4654 11.9651L9.46757 11.9628L8.38379 10.9258ZM8.95508 10.2031L7.57005 9.62717L7.56863 9.63063L8.95508 10.2031ZM9.0498 9.33203L7.5501 9.3013L7.54998 9.30918L9.0498 9.33203ZM11.332 7.0498L11.3092 5.54998L11.309 5.54998L11.332 7.0498ZM12.2031 6.95508L11.632 5.56804L11.6242 5.57132L12.2031 6.95508ZM12.9258 6.38379L13.9628 7.46757L13.9643 7.46615L12.9258 6.38379ZM16.8086 10.5381L16.2346 9.15227L16.2344 9.15235L16.8086 10.5381ZM16.6465 10.6465L15.5858 9.58582L16.6465 10.6465ZM13.5 13.793L12.4393 14.8536L13.5 15.9143L14.5607 14.8536L13.5 13.793ZM11.5 13H10H11.5ZM13.1465 14.8535L14.2077 13.7934L14.2071 13.7929L13.1465 14.8535ZM13.3086 14.9619L12.734 16.3475L12.7342 16.3476L13.3086 14.9619ZM13.6914 14.9619L14.2658 16.3476L14.266 16.3475L13.6914 14.9619ZM13.8535 14.8535L12.7929 13.7929L12.7923 13.7934L13.8535 14.8535ZM17.4619 11.1914L18.8477 11.7656L18.8477 11.7654L17.4619 11.1914ZM17.5 11H19H17.5ZM17.4619 10.8086L18.8477 10.2346L18.8477 10.2344L17.4619 10.8086ZM17.3535 10.6465L16.2929 11.7071L17.3535 10.6465ZM17.1914 10.5381L17.7656 9.15235L17.7654 9.15227L17.1914 10.5381ZM14.5 5.5V7C14.4454 7 14.4475 6.97529 14.5478 7.04106C14.67 7.12113 14.8024 7.24194 15.0349 7.4654L16.0742 6.38379L17.1135 5.30218C16.7829 4.98446 15.8588 4 14.5 4V5.5ZM16.0742 6.38379L15.0372 7.46757C15.151 7.57647 15.3146 7.73409 15.4833 7.87493C15.6393 8.00512 15.8988 8.20711 16.2244 8.34153L16.7969 6.95508L17.3694 5.56863C17.4025 5.58232 17.4261 5.59459 17.4394 5.60194C17.4463 5.60576 17.4513 5.60878 17.4544 5.61071C17.4576 5.61265 17.4592 5.61372 17.4592 5.61374C17.4593 5.61376 17.458 5.61292 17.4554 5.61108C17.4529 5.60924 17.4492 5.60657 17.4445 5.60296C17.4349 5.59561 17.422 5.58541 17.4056 5.57172C17.3302 5.50875 17.2436 5.42666 17.1112 5.30001L16.0742 6.38379ZM16.7969 6.95508L16.2209 8.3401C16.5463 8.4754 16.8735 8.5118 17.0631 8.52739C17.2739 8.54472 17.4937 8.54732 17.6451 8.54963L17.668 7.0498L17.6908 5.54998C17.6063 5.54869 17.5355 5.54761 17.468 5.54554C17.4011 5.5435 17.3495 5.54081 17.309 5.53748C17.2672 5.53404 17.251 5.531 17.2532 5.53142C17.2553 5.53181 17.2665 5.534 17.285 5.5392C17.3033 5.54432 17.3338 5.55381 17.3728 5.57006L16.7969 6.95508ZM17.668 7.0498L17.6449 8.54963C17.9758 8.55471 18.1576 8.56187 18.3047 8.59219C18.4229 8.61656 18.4164 8.63732 18.3895 8.61046L19.4502 7.5498L20.5109 6.48914C19.5287 5.50697 18.1653 5.55727 17.691 5.54998L17.668 7.0498ZM19.4502 7.5498L18.3895 8.61046C18.3627 8.58361 18.3834 8.57707 18.4078 8.69527C18.4381 8.84237 18.4453 9.02422 18.4504 9.35508L19.9502 9.33203L21.45 9.30898C21.4427 8.83473 21.493 7.47132 20.5109 6.48914L19.4502 7.5498ZM19.9502 9.33203L18.4504 9.35489C18.4527 9.50627 18.4553 9.72605 18.4726 9.9369C18.4882 10.1265 18.5246 10.4537 18.6599 10.7791L20.0449 10.2031L21.4299 9.62718C21.4462 9.66624 21.4557 9.69672 21.4608 9.71495C21.466 9.73346 21.4682 9.74474 21.4686 9.74681C21.469 9.74897 21.466 9.73282 21.4625 9.69104C21.4592 9.65047 21.4565 9.59887 21.4545 9.53201C21.4524 9.46451 21.4513 9.39372 21.45 9.30918L19.9502 9.33203ZM20.0449 10.2031L18.6579 10.7742C18.7924 11.101 18.9949 11.3611 19.1254 11.5173C19.2662 11.686 19.4242 11.8497 19.5324 11.9628L20.6162 10.9258L21.7 9.88876C21.5727 9.75572 21.4909 9.66964 21.428 9.59427C21.4143 9.57794 21.4042 9.56519 21.397 9.55565C21.3934 9.55097 21.3908 9.54741 21.389 9.54491C21.3872 9.5424 21.3864 9.54123 21.3865 9.54133C21.3865 9.54142 21.3876 9.54311 21.3896 9.54633C21.3916 9.54954 21.3947 9.55467 21.3985 9.56165C21.406 9.57507 21.4183 9.59876 21.4319 9.632L20.0449 10.2031ZM20.6162 10.9258L19.5338 11.9643C19.758 12.1979 19.8788 12.3302 19.9591 12.4526C20.0248 12.5529 20 12.5547 20 12.5H21.5H23C23 11.1412 22.015 10.2171 21.6986 9.88728L20.6162 10.9258ZM21.5 12.5H20C20 12.4454 20.0247 12.4475 19.9589 12.5478C19.8789 12.67 19.7581 12.8024 19.5346 13.0349L20.6162 14.0742L21.6978 15.1135C22.0155 14.7829 23 13.8588 23 12.5H21.5ZM20.6162 14.0742L19.5324 13.0372C19.4235 13.151 19.2659 13.3146 19.1251 13.4833C18.9949 13.6393 18.7929 13.8988 18.6585 14.2244L20.0449 14.7969L21.4314 15.3694C21.4177 15.4025 21.4054 15.4261 21.3981 15.4394C21.3942 15.4463 21.3912 15.4513 21.3893 15.4544C21.3874 15.4576 21.3863 15.4592 21.3863 15.4592C21.3862 15.4593 21.3871 15.458 21.3889 15.4554C21.3908 15.4529 21.3934 15.4492 21.397 15.4445C21.4044 15.4349 21.4146 15.422 21.4283 15.4056C21.4912 15.3302 21.5733 15.2436 21.7 15.1112L20.6162 14.0742ZM20.0449 14.7969L18.6599 14.2209C18.5246 14.5463 18.4882 14.8735 18.4726 15.0631C18.4553 15.2739 18.4527 15.4937 18.4504 15.6451L19.9502 15.668L21.45 15.6908C21.4513 15.6063 21.4524 15.5355 21.4545 15.468C21.4565 15.4011 21.4592 15.3495 21.4625 15.309C21.466 15.2672 21.469 15.251 21.4686 15.2532C21.4682 15.2553 21.466 15.2665 21.4608 15.285C21.4557 15.3033 21.4462 15.3338 21.4299 15.3728L20.0449 14.7969ZM19.9502 15.668L18.4504 15.6449C18.4453 15.9758 18.4381 16.1576 18.4078 16.3047C18.3834 16.4229 18.3627 16.4164 18.3895 16.3895L19.4502 17.4502L20.5109 18.5109C21.493 17.5287 21.4427 16.1653 21.45 15.691L19.9502 15.668ZM19.4502 17.4502L18.3895 16.3895C18.4164 16.3627 18.4229 16.3834 18.3047 16.4078C18.1576 16.4381 17.9758 16.4453 17.6449 16.4504L17.668 17.9502L17.691 19.45C18.1653 19.4427 19.5287 19.493 20.5109 18.5109L19.4502 17.4502ZM17.668 17.9502L17.6451 16.4504C17.4937 16.4527 17.2739 16.4553 17.0631 16.4726C16.8735 16.4882 16.5463 16.5246 16.2209 16.6599L16.7969 18.0449L17.3728 19.4299C17.3338 19.4462 17.3033 19.4557 17.285 19.4608C17.2665 19.466 17.2553 19.4682 17.2532 19.4686C17.251 19.469 17.2672 19.466 17.309 19.4625C17.3495 19.4592 17.4011 19.4565 17.468 19.4545C17.5355 19.4524 17.6063 19.4513 17.6908 19.45L17.668 17.9502ZM16.7969 18.0449L16.2257 16.6579C15.899 16.7924 15.6389 16.9949 15.4827 17.1254C15.314 17.2662 15.1503 17.4242 15.0372 17.5324L16.0742 18.6162L17.1112 19.7C17.2443 19.5727 17.3304 19.4909 17.4057 19.428C17.4221 19.4143 17.4348 19.4042 17.4443 19.397C17.449 19.3934 17.4526 19.3908 17.4551 19.389C17.4576 19.3872 17.4588 19.3864 17.4587 19.3865C17.4586 19.3865 17.4569 19.3876 17.4537 19.3896C17.4505 19.3916 17.4453 19.3947 17.4383 19.3985C17.4249 19.406 17.4012 19.4183 17.368 19.4319L16.7969 18.0449ZM16.0742 18.6162L15.0357 17.5338C14.8021 17.758 14.6698 17.8788 14.5474 17.9591C14.4471 18.0248 14.4453 18 14.5 18V19.5V21C15.8588 21 16.7829 20.015 17.1127 19.6986L16.0742 18.6162ZM14.5 19.5V18C14.5546 18 14.5525 18.0247 14.4522 17.9589C14.33 17.8789 14.1976 17.7581 13.9651 17.5346L12.9258 18.6162L11.8865 19.6978C12.2172 20.0155 13.1412 21 14.5 21V19.5ZM12.9258 18.6162L13.9628 17.5324C13.849 17.4235 13.6854 17.2659 13.5167 17.1251C13.3607 16.9949 13.1012 16.7929 12.7756 16.6585L12.2031 18.0449L11.6306 19.4314C11.5975 19.4177 11.5739 19.4054 11.5606 19.3981C11.5537 19.3942 11.5487 19.3912 11.5456 19.3893C11.5424 19.3874 11.5408 19.3863 11.5408 19.3863C11.5407 19.3862 11.542 19.3871 11.5446 19.3889C11.5471 19.3908 11.5508 19.3934 11.5555 19.397C11.5651 19.4044 11.578 19.4146 11.5944 19.4283C11.6698 19.4912 11.7564 19.5733 11.8888 19.7L12.9258 18.6162ZM12.2031 18.0449L12.7791 16.6599C12.4537 16.5246 12.1265 16.4882 11.9369 16.4726C11.7261 16.4553 11.5063 16.4527 11.3549 16.4504L11.332 17.9502L11.3092 19.45C11.3937 19.4513 11.4645 19.4524 11.532 19.4545C11.5989 19.4565 11.6505 19.4592 11.691 19.4625C11.7328 19.466 11.749 19.469 11.7468 19.4686C11.7447 19.4682 11.7335 19.466 11.715 19.4608C11.6967 19.4557 11.6662 19.4462 11.6272 19.4299L12.2031 18.0449ZM11.332 17.9502L11.3551 16.4504C11.0242 16.4453 10.8424 16.4381 10.6953 16.4078C10.5771 16.3834 10.5836 16.3627 10.6105 16.3895L9.5498 17.4502L8.48914 18.5109C9.47132 19.493 10.8347 19.4427 11.309 19.45L11.332 17.9502ZM9.5498 17.4502L10.6105 16.3895C10.6373 16.4164 10.6166 16.4229 10.5922 16.3047C10.5619 16.1576 10.5547 15.9758 10.5496 15.6449L9.0498 15.668L7.54998 15.691C7.55727 16.1653 7.50697 17.5287 8.48914 18.5109L9.5498 17.4502ZM9.0498 15.668L10.5496 15.6451C10.5473 15.4937 10.5447 15.2739 10.5274 15.0631C10.5118 14.8735 10.4754 14.5463 10.3401 14.2209L8.95508 14.7969L7.57006 15.3728C7.55381 15.3338 7.54432 15.3033 7.5392 15.285C7.534 15.2665 7.53181 15.2553 7.53142 15.2532C7.531 15.251 7.53404 15.2672 7.53748 15.309C7.54081 15.3495 7.5435 15.4011 7.54554 15.468C7.54761 15.5355 7.54869 15.6063 7.54998 15.6908L9.0498 15.668ZM8.95508 14.7969L10.3421 14.2257C10.2076 13.899 10.0051 13.6389 9.87463 13.4827C9.73377 13.314 9.57582 13.1503 9.46757 13.0372L8.38379 14.0742L7.30001 15.1112C7.4273 15.2443 7.50908 15.3304 7.57202 15.4057C7.58566 15.4221 7.59578 15.4348 7.60305 15.4443C7.60662 15.449 7.60924 15.4526 7.61103 15.4551C7.61283 15.4576 7.61361 15.4588 7.61355 15.4587C7.61348 15.4586 7.61236 15.4569 7.61038 15.4537C7.6084 15.4505 7.60534 15.4453 7.60148 15.4383C7.59405 15.4249 7.58175 15.4012 7.56806 15.368L8.95508 14.7969ZM8.38379 14.0742L9.46615 13.0357C9.24198 12.8021 9.1212 12.6698 9.04095 12.5474C8.97519 12.4471 9 12.4453 9 12.5H7.5H6C6 13.8588 6.98502 14.7829 7.30143 15.1127L8.38379 14.0742ZM7.5 12.5H9C9 12.5546 8.97529 12.5525 9.04106 12.4522C9.12113 12.33 9.24194 12.1976 9.4654 11.9651L8.38379 10.9258L7.30218 9.88649C6.98446 10.2172 6 11.1412 6 12.5H7.5ZM8.38379 10.9258L9.46757 11.9628C9.57647 11.849 9.73409 11.6854 9.87493 11.5167C10.0051 11.3607 10.2071 11.1012 10.3415 10.7756L8.95508 10.2031L7.56863 9.63063C7.58232 9.59748 7.59459 9.5739 7.60194 9.56065C7.60576 9.55374 7.60878 9.54869 7.61071 9.54555C7.61265 9.54241 7.61372 9.5408 7.61374 9.54078C7.61376 9.54075 7.61292 9.542 7.61108 9.54457C7.60924 9.54713 7.60657 9.55076 7.60296 9.55549C7.59561 9.56514 7.58541 9.578 7.57172 9.5944C7.50875 9.66984 7.42666 9.7564 7.30001 9.88876L8.38379 10.9258ZM8.95508 10.2031L10.3401 10.7791C10.4754 10.4537 10.5118 10.1265 10.5274 9.9369C10.5447 9.72605 10.5473 9.50627 10.5496 9.35489L9.0498 9.33203L7.54998 9.30918C7.54869 9.39372 7.54761 9.46451 7.54554 9.53201C7.5435 9.59887 7.54081 9.65047 7.53748 9.69104C7.53404 9.73282 7.531 9.74897 7.53142 9.74681C7.53181 9.74474 7.534 9.73346 7.5392 9.71495C7.54432 9.69672 7.55381 9.66624 7.57006 9.62718L8.95508 10.2031ZM9.0498 9.33203L10.5495 9.36276C10.5564 9.02475 10.563 8.84355 10.5937 8.69349C10.6182 8.57409 10.6388 8.58216 10.6105 8.61046L9.5498 7.5498L8.48914 6.48914C7.50681 7.47148 7.55953 8.84188 7.55012 9.3013L9.0498 9.33203ZM9.5498 7.5498L10.6105 8.61046C10.5836 8.63732 10.5771 8.61656 10.6953 8.59219C10.8424 8.56187 11.0242 8.55471 11.3551 8.54963L11.332 7.0498L11.309 5.54998C10.8347 5.55727 9.47132 5.50697 8.48914 6.48914L9.5498 7.5498ZM11.332 7.0498L11.3549 8.54963C11.5062 8.54732 11.7263 8.54472 11.9372 8.5274C12.1259 8.5119 12.4553 8.47559 12.7821 8.33884L12.2031 6.95508L11.6242 5.57132C11.6642 5.55455 11.6955 5.54477 11.7142 5.53951C11.7332 5.53416 11.7448 5.53189 11.7471 5.53146C11.7496 5.53099 11.7336 5.53402 11.6916 5.53747C11.651 5.54081 11.5992 5.5435 11.5322 5.54554C11.4646 5.5476 11.3937 5.54869 11.3092 5.54998L11.332 7.0498ZM12.2031 6.95508L12.7742 8.3421C13.101 8.20755 13.3611 8.0051 13.5173 7.87463C13.686 7.73377 13.8497 7.57582 13.9628 7.46757L12.9258 6.38379L11.8888 5.30001C11.7557 5.4273 11.6696 5.50908 11.5943 5.57202C11.5779 5.58566 11.5652 5.59578 11.5557 5.60305C11.551 5.60662 11.5474 5.60924 11.5449 5.61103C11.5424 5.61283 11.5412 5.61361 11.5413 5.61355C11.5414 5.61348 11.5431 5.61236 11.5463 5.61038C11.5495 5.6084 11.5547 5.60534 11.5617 5.60148C11.5751 5.59405 11.5988 5.58175 11.632 5.56806L12.2031 6.95508ZM12.9258 6.38379L13.9643 7.46615C14.1979 7.24198 14.3302 7.1212 14.4526 7.04095C14.5529 6.97519 14.5547 7 14.5 7V5.5V4C13.1412 4 12.2171 4.98502 11.8873 5.30143L12.9258 6.38379ZM17 10.5V9C16.7371 9 16.4771 9.05182 16.2346 9.15227L16.8086 10.5381L17.3826 11.9239C17.2615 11.9741 17.1315 12 17 12V10.5ZM16.8086 10.5381L16.2344 9.15235C15.991 9.25319 15.7709 9.40074 15.5858 9.58582L16.6465 10.6465L17.7071 11.7071C17.6149 11.7994 17.5048 11.8733 17.3828 11.9238L16.8086 10.5381ZM16.6465 10.6465L15.5858 9.58582L12.4393 12.7323L13.5 13.793L14.5607 14.8536L17.7071 11.7071L16.6465 10.6465ZM13.5 13.793L14.5607 12.7323L13.4142 11.5858L12.3535 12.6465L11.2929 13.7071L12.4393 14.8536L13.5 13.793ZM12.3535 12.6465L13.4142 11.5858C13.0393 11.211 12.5309 11 12 11V12.5V14C11.7344 14 11.48 13.8943 11.2929 13.7071L12.3535 12.6465ZM12 12.5V11C11.4691 11 10.9607 11.211 10.5858 11.5858L11.6465 12.6465L12.7071 13.7071C12.52 13.8943 12.2656 14 12 14V12.5ZM11.6465 12.6465L10.5858 11.5858C10.211 11.9607 10 12.4691 10 13H11.5H13C13 13.2656 12.8943 13.52 12.7071 13.7071L11.6465 12.6465ZM11.5 13H10C10 13.5309 10.211 14.0393 10.5858 14.4142L11.6465 13.3535L12.7071 12.2929L11.6465 13.3535ZM11.6465 13.3535L10.5858 14.4142L12.0858 15.9142L13.1465 14.8535L14.2071 13.7929L12.7071 12.2929L11.6465 13.3535ZM13.1465 14.8535L12.0852 15.9136C12.2712 16.0998 12.4917 16.247 12.734 16.3475L13.3086 14.9619L13.8832 13.5763C14.0041 13.6265 14.1146 13.7002 14.2077 13.7934L13.1465 14.8535ZM13.3086 14.9619L12.7342 16.3476C12.9772 16.4483 13.2374 16.5 13.5 16.5V15V13.5C13.6312 13.5 13.7614 13.5258 13.883 13.5763L13.3086 14.9619ZM13.5 15V16.5C13.7626 16.5 14.0228 16.4483 14.2658 16.3476L13.6914 14.9619L13.117 13.5763C13.2386 13.5258 13.3688 13.5 13.5 13.5V15ZM13.6914 14.9619L14.266 16.3475C14.5083 16.247 14.7288 16.0998 14.9148 15.9136L13.8535 14.8535L12.7923 13.7934C12.8854 13.7002 12.9959 13.6265 13.1168 13.5763L13.6914 14.9619ZM13.8535 14.8535L14.9142 15.9142L18.4142 12.4142L17.3535 11.3535L16.2929 10.2929L12.7929 13.7929L13.8535 14.8535ZM17.3535 11.3535L18.4142 12.4142C18.5993 12.2291 18.7468 12.009 18.8477 11.7656L17.4619 11.1914L16.0762 10.6172C16.1267 10.4952 16.2006 10.3851 16.2929 10.2929L17.3535 11.3535ZM17.4619 11.1914L18.8477 11.7654C18.9482 11.5229 19 11.2629 19 11H17.5H16C16 10.8685 16.0259 10.7385 16.0761 10.6174L17.4619 11.1914ZM17.5 11H19C19 10.7371 18.9482 10.4771 18.8477 10.2346L17.4619 10.8086L16.0761 11.3826C16.0259 11.2615 16 11.1315 16 11H17.5ZM17.4619 10.8086L18.8477 10.2344C18.7468 9.99102 18.5993 9.77091 18.4142 9.58582L17.3535 10.6465L16.2929 11.7071C16.2006 11.6149 16.1267 11.5048 16.0762 11.3828L17.4619 10.8086ZM17.3535 10.6465L18.4142 9.58582C18.2291 9.40074 18.009 9.25319 17.7656 9.15235L17.1914 10.5381L16.6172 11.9238C16.4952 11.8733 16.3851 11.7994 16.2929 11.7071L17.3535 10.6465ZM17.1914 10.5381L17.7654 9.15227C17.5229 9.05182 17.2629 9 17 9V10.5V12C16.8685 12 16.7385 11.9741 16.6174 11.9239L17.1914 10.5381Z"
          fill="#FAFAFA"
          mask={`url(#${maskId})`}
        />
      </g>
      <defs>
        <filter
          id={filterId}
          x="0"
          y="0"
          width="29"
          height="29"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="2" />
          <feGaussianBlur stdDeviation="3" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.027451 0 0 0 0 0.027451 0 0 0 0 0.0313726 0 0 0 0.12 0"
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="1" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.027451 0 0 0 0 0.027451 0 0 0 0 0.0313726 0 0 0 0.08 0"
          />
          <feBlend mode="normal" in2="effect1_dropShadow" result="effect2_dropShadow" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow" result="shape" />
        </filter>
      </defs>
    </>
  );
}

/** "Sociability Verified" blue tick badge, nested inside the composed pin's
 * own <svg> and positioned top-right via x/y. */
function VerifiedTickBadge({ isDark }: { isDark: boolean }) {
  return (
    <svg
      x={TICK_BADGE_X}
      y={TICK_BADGE_Y}
      width={TICK_SVG_SIZE}
      height={TICK_SVG_SIZE}
      viewBox="0 0 29 29"
      fill="none"
      aria-hidden="true"
    >
      <TickBadgeGraphic isDark={isDark} />
    </svg>
  );
}

/** Same tick badge as VerifiedTickBadge, but standalone (its own top-level
 * <svg>, CSS-positioned) for overlaying on the flat classic <img> pin used
 * when a venue is selected — that pin isn't a nested <svg> we can drop x/y into. */
function VerifiedTickOverlay({ size, isDark }: { size: number; isDark: boolean }) {
  const overlaySize = (TICK_SVG_SIZE / PIN_W) * size;
  return (
    <svg
      width={overlaySize}
      height={overlaySize}
      viewBox="0 0 29 29"
      fill="none"
      aria-hidden="true"
      style={{
        position: "absolute",
        left: (TICK_BADGE_X / PIN_W) * size,
        top: (TICK_BADGE_Y / PIN_H) * size,
      }}
    >
      <TickBadgeGraphic isDark={isDark} />
    </svg>
  );
}

/** Verified (has-data) pin: hollow circle-head + tail (grey outline, white
 * interior) with a blue "Sociability Verified" tick badge top-right. */
export function VerifiedSociabilityPin({
  size,
  accent = ORANGE,
  isHovered = false,
  isDark = false,
}: {
  size: number;
  accent?: string;
  isHovered?: boolean;
  isDark?: boolean;
}) {
  return (
    <svg
      width={size}
      height={size * PIN_ASPECT}
      viewBox="0 0 42 38"
      fill="none"
      style={{
        display: "block",
        transform: `translateX(${(FRAME_SHIFT_X / PIN_W) * size}px)`,
      }}
      aria-hidden="true"
    >
      <circle cx="18.5" cy="16.5" r="16.5" fill={accent} />
      <path d={HEAD_TAIL_PATH} fill={accent} />
      {/* Floods with the body's own colour, so the face and ring read as one pin. */}
      <circle cx="18.5" cy="16.5" r="14.5" fill={isHovered ? accent : PIN_FACE} />
      {isHovered && (
        <circle
          cx="18.5"
          cy="16.5"
          r={HOVER_RING_R}
          fill="none"
          stroke={ON_FILLED}
          strokeWidth={HOVER_RING_W}
        />
      )}

      {/* "Sociability Verified" badge: blue tick, only shown when a
          team-approved review grants it (see hasVerifiedData). */}
      <VerifiedTickBadge isDark={isDark} />
    </svg>
  );
}

/** Same hollow ring + tail body as the verified pin, recolorable and with
 * no badge at all (used for the unmapped/grey venue in the auto-suggest list). */
export function PlainRingPin({
  size,
  color,
  isHovered = false,
}: {
  size: number;
  color: string;
  isHovered?: boolean;
}) {
  return (
    <svg
      width={size}
      height={size * PIN_ASPECT}
      viewBox="0 0 42 38"
      fill="none"
      style={{
        display: "block",
        transform: `translateX(${(FRAME_SHIFT_X / PIN_W) * size}px)`,
      }}
      aria-hidden="true"
    >
      <circle cx="18.5" cy="16.5" r="16.5" fill={color} />
      <path d={HEAD_TAIL_PATH} fill={color} />
      {/* Floods with the body's own colour on hover, not the shared orange. */}
      <circle cx="18.5" cy="16.5" r="14.5" fill={isHovered ? color : PIN_FACE} />
      {isHovered && (
        <circle
          cx="18.5"
          cy="16.5"
          r={HOVER_RING_R}
          fill="none"
          stroke={ON_FILLED}
          strokeWidth={HOVER_RING_W}
        />
      )}
    </svg>
  );
}

interface AdvancedPinContentProps {
  venue: Venue;
  isSelected: boolean;
  isHovered: boolean;
  isMapperView: boolean;
  /** Always show the venue name label (Sociability mode, un-clustered pins). */
  showLabel?: boolean;
  /** Suppress the always-on inline name; still shown on hover/selection. */
  suppressLabel?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

/**
 * HTML content for an Advanced Marker pin, composed at runtime from a colored
 * frame, a business-type glyph and (outside mapper view) a badge. The name
 * label sits to the right of the pin, Google POI style.
 */
function AdvancedPinContentImpl({
  venue,
  isSelected,
  isHovered,
  isMapperView,
  showLabel = false,
  suppressLabel = false,
  onMouseEnter,
  onMouseLeave,
}: AdvancedPinContentProps) {
  const isDark = useDarkMode();
  const isActive = isSelected || isHovered;
  const size = isSelected ? SIZE_SELECTED : isActive ? SIZE_ACTIVE : SIZE_DEFAULT;

  const isGrey = venue.venuePinType === VenuePinType.Grey;
  // Blue "Sociability Verified" tick: only an approved/team review grants it —
  // experiences and alerts never do (see hasVerifiedData for why).
  const isVerified = !isGrey && hasVerifiedData(venue);
  const height = size * PIN_ASPECT;
  const color = getVenuePinColor(venue.venuePinType, isMapperView, isDark);
  const glyphSize = size * 0.4;
  const category = venue.calcVenueType || venue.businessType || "";
  const centreTop = getPinCentreTop(size);

  // Mapper pins and the orange pin flood their face on hover; the flat grey pin
  // doesn't, so only the flooded ones invert their glyph to stay legible.
  const isFaceFlooded = isHovered && (isMapperView || !isGrey);

  const glyph = (
    <span
      className="absolute pointer-events-none"
      style={{ left: "50%", top: centreTop, transform: "translate(-50%, -50%)" }}
    >
      <BusinessTypeGlyph
        businessType={venue.businessType}
        size={glyphSize}
        color={isFaceFlooded ? ON_FILLED : GLYPH_COLOR}
      />
    </span>
  );

  return (
    <div
      role="img"
      aria-label={venue.name || "Map pin"}
      className="adv-pin-content relative flex items-end justify-center cursor-pointer"
      style={{ width: size, height }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {isSelected ? (
        // Highlighted venue: classic flat Sociability pin, without the
        // verified/purple border — the "old" pins we used to show. Still
        // gets the blue "Sociability Verified" tick, overlaid via CSS
        // since this pin is a flat <img>, not a composed <svg>.
        <div className="relative" style={{ width: size, height: size }}>
          <img
            src={getSelectedPinAsset(venue.venuePinType, isMapperView, isDark)}
            width={size}
            height={size}
            alt={venue.name || ""}
            draggable={false}
            style={{ display: "block" }}
          />
          {!isMapperView && isVerified && (
            <VerifiedTickOverlay size={size} isDark={isDark} />
          )}
        </div>
      ) : isMapperView ? (
        // Same composed pin as user view, in the state's colour and badge-free.
        <>
          <PlainRingPin size={size} color={color} isHovered={isHovered} />
          {glyph}
        </>
      ) : (
        <>
          {isGrey ? (
            <SociabilityPin size={size} color={color} />
          ) : isVerified ? (
            <VerifiedSociabilityPin
              size={size}
              accent={color}
              isHovered={isHovered}
              isDark={isDark}
            />
          ) : (
            <PlainRingPin size={size} color={color} isHovered={isHovered} />
          )}
          {glyph}
        </>
      )}

      {/* Name + category, right of the pin (Google POI style). */}
      {venue.name && (!suppressLabel || isSelected || isHovered) && (
        <div
          className={cn(
            "absolute left-full ml-1.5 flex flex-col",
            "transition-opacity duration-150 pointer-events-none select-none",
            isSelected || isHovered || showLabel ? "opacity-100" : "opacity-0",
          )}
          style={{ top: centreTop, transform: "translateY(-55%)" }}
        >
          <span
            className="text-[13px] font-medium leading-tight whitespace-nowrap max-w-[160px] truncate"
            style={{ textShadow: NAME_HALO, color: NAME_TEXT_COLOR }}
          >
            {venue.name}
          </span>
          {category && (
            <span
              className="text-[11px] leading-tight whitespace-nowrap max-w-[160px] truncate"
              style={{ textShadow: NAME_HALO, color: CATEGORY_TEXT_COLOR }}
            >
              {category}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// Memoized so hovering/selecting one pin doesn't re-render all the others.
// Handler props are intentionally ignored: they only close over the (stable)
// venue + setState, so a skipped re-render keeps behaviour identical.
export const AdvancedPinContent = memo(
  AdvancedPinContentImpl,
  (prev, next) =>
    prev.venue === next.venue &&
    prev.isSelected === next.isSelected &&
    prev.isHovered === next.isHovered &&
    prev.isMapperView === next.isMapperView &&
    prev.showLabel === next.showLabel &&
    prev.suppressLabel === next.suppressLabel,
);
